import { useCallback } from 'react';
import {
  useFormStoreEvents,
  useMediaResource,
} from '@brightmls/nextgen.lae.flc';
import { useService } from '../../../../contexts/ServiceContext';
import { MediaField } from '../../../../system/field';
import {
  useMediaStore,
  type InitiateUploadHandler,
  type UploadFileHandler,
  type SaveMetadataHandler,
  type SaveOrderHandler,
  type DeleteHandler,
  type RefreshHandler,
  type RetryJobHandler,
  type RetryAiScanHandler,
  type AddVirtualTourHandler,
  OfficeExclusiveType,
} from '@brightmls/nextgen.lae.mediacenter';
import type { MediaUploadRequest } from '../../../../contexts/ServiceContext';
import { useMediaLogger } from './useMediaLogger';
import { MediaCategory } from '../../../../system/media';

interface UseMediaHandlersOptions {
  /** WebSocket connectionId for real-time upload updates (from WHOAMI) */
  connectionId?: string | null;
  /** Listing ID for AI processing optimization */
  listingId?: string | null;
}

export function useMediaHandlers(
  listingKey: string,
  options: UseMediaHandlersOptions = {},
) {
  const { connectionId, listingId } = options;
  const {
    initiateMediaUpload,
    saveListingResources,
    deleteListingResources,
    cancelMediaJob,
    retryMediaJob,
    retryAiScan,
    updateMedia,
    deleteMedia,
    saveListingTour,
  } = useService();
  const logger = useMediaLogger('useMediaHandlers');
  const { updateFromListingStateAfter } = useFormStoreEvents();

  // Get FLC media for syncing after operations
  const { media: flcMedia, updateMediaByCategory } = useMediaResource();

  // Get items from store to look up categories when deleting
  const items = useMediaStore((state) => state.items);

  const handleInitiateUpload: InitiateUploadHandler = useCallback(
    async (requests) => {
      const uploadRequests: MediaUploadRequest[] = requests.map(
        ({
          file,
          item,
          flags,
          jobId,
          uploadMode,
          replacingMediaKey,
          originalMetadata,
        }) => ({
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          category: item.MEDIA_CATEGORY,
          listingKey,
          // Include listingId for AI processing optimization
          listingId: listingId || undefined,
          // Include media metadata
          mediaItemNumber: item.MEDIA_ITEM_NUMBER,
          mediaDisplayOrder: item.MEDIA_DISPLAY_ORDER,
          visibility: item.MEDIA_VISIBILITY,
          // AI processing flags
          scanForObjects: flags.scanForObjects,
          generateDescriptions: flags.generateDescriptions,
          jobId,
          // Include connectionId for real-time updates via WebSocket
          connectionId: connectionId || undefined,
          // Upload mode for replacement handling
          uploadMode,
          replacingMediaKey,
          originalMetadata,
          ...(item.DOCUMENT_TYPE && { docType: item.DOCUMENT_TYPE }),
        }),
      );

      return await initiateMediaUpload(uploadRequests);
    },
    [initiateMediaUpload, connectionId, listingId],
  );

  const handleUploadFile: UploadFileHandler = useCallback(
    async (url, file, onProgress) => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    },
    [],
  );

  const handleSaveMetadata: SaveMetadataHandler = useCallback(
    async (item) => {
      try {
        const updates: {
          sortOrder?: number;
          description?: string;
          longDescription?: string;
          url?: string;
          visibility?: string;
        } = {
          sortOrder: item.MEDIA_DISPLAY_ORDER,
          description: item.MEDIA_SHORT_DESCRIPTION,
          longDescription: item.MEDIA_LONG_DESCRIPTION,
          visibility: item.MEDIA_VISIBILITY,
        };

        // For tour categories, include URL in the update
        const tourCategories = [
          'Unbranded Virtual Tour',
          'Branded Virtual Tour',
        ];
        if (tourCategories.includes(item.MEDIA_CATEGORY) && item.MEDIA_URL) {
          updates.url = item.MEDIA_URL;
        }

        await updateMedia(
          listingKey,
          item.MED_VW_KEY,
          item.MEDIA_CATEGORY,
          updates,
        );

        // Sync updated metadata back to FLC so publish sends current data
        let mediaCategory: MediaCategory | undefined;
        if (item.MEDIA_CATEGORY === 'Photo') {
          mediaCategory = MediaCategory.Photo;
        } else if (item.MEDIA_CATEGORY === 'Document') {
          mediaCategory = MediaCategory.Document;
        } else if (item.MEDIA_CATEGORY === 'Floor Plan') {
          mediaCategory = MediaCategory.FloorPlan;
        } else if (
          item.MEDIA_CATEGORY === 'Unbranded Virtual Tour' ||
          item.MEDIA_CATEGORY === 'Branded Virtual Tour'
        ) {
          mediaCategory = MediaCategory.Tour;
        }

        if (mediaCategory) {
          const categoryRecords = (flcMedia as any[]).filter(
            (m) => m.MEDIA_CATEGORY === item.MEDIA_CATEGORY,
          );
          const updatedRecords = categoryRecords.map((m) => {
            if (m.MED_VW_KEY !== item.MED_VW_KEY) return m;

            const merged: any = {
              ...m,
              MEDIA_DISPLAY_ORDER: item.MEDIA_DISPLAY_ORDER,
              MEDIA_SHORT_DESCRIPTION: item.MEDIA_SHORT_DESCRIPTION,
              MEDIA_LONG_DESCRIPTION: item.MEDIA_LONG_DESCRIPTION,
              MEDIA_VISIBILITY: item.MEDIA_VISIBILITY,
            };

            // For tours, sync all URL variant fields so publish
            // doesn't overwrite the lambda's CWA updates with stale values
            if (item.MEDIA_URL) {
              merged.MEDIA_URL = item.MEDIA_URL;
              merged.MEDIA_URL_FULL = item.MEDIA_URL;
              merged.MEDIA_URLHD = item.MEDIA_URL;
              merged.MEDIA_URL_HI_RES = item.MEDIA_URL;
              merged.MEDIA_URL_MEDIUM = item.MEDIA_URL;
              merged.MEDIA_URL_THUMB = item.MEDIA_URL;
              merged.MEDIA_FILE_NAME = item.MEDIA_URL;
              merged.MEDIA_SOURCE_FILE_NAME = item.MEDIA_URL;
            }

            return merged;
          });
          updateMediaByCategory({
            category: mediaCategory,
            resources: updatedRecords,
          });
        }
      } catch (error) {
        logger.error('Failed to save metadata:', error);
        throw error;
      }
    },
    [listingKey, updateMedia, flcMedia, updateMediaByCategory, logger],
  );

  const handleSaveOrder: SaveOrderHandler = useCallback(
    async (items) => {
      logger.log('TODO: save order handler');
    },
    [listingKey, saveListingResources, logger],
  );

  const handleDelete: DeleteHandler = useCallback(
    async (keys: string[], jobIds?: string[]) => {
      let includesOEDocument = false;
      try {
        // Cancel any in-progress media jobs first (cleanup S3 + DynamoDB job records)
        if (jobIds && jobIds.length > 0) {
          const cancelResults = await Promise.allSettled(
            jobIds.map((jobId) => cancelMediaJob(jobId, listingKey)),
          );

          // Log any failures but don't block the delete
          const failures = cancelResults.filter((r) => r.status === 'rejected');
          if (failures.length > 0) {
            logger.warn(
              `Failed to cancel ${failures.length} media jobs:`,
              failures,
            );
          }
        }

        // Look up items to get their categories (before deletion)
        const itemsToDelete = keys
          .map((key) => items.find((item) => item.MED_VW_KEY === key))
          .filter((item): item is NonNullable<typeof item> => item != null);

        includesOEDocument = itemsToDelete.some(
          (item) => item.DOCUMENT_TYPE === OfficeExclusiveType.OFFICE_EXCLUSIVE,
        );

        // Delete the actual media resources using the batch media-delete endpoint
        if (keys.length > 0) {
          const deletePayload = itemsToDelete.map((item) => ({
            mediaKey: item.MED_VW_KEY,
            category: item.MEDIA_CATEGORY,
          }));

          if (deletePayload.length > 0) {
            const result = await deleteMedia(listingKey, deletePayload);

            // Check for any failures in the batch response
            const failures = result.results.filter((d) => !d.success);
            if (failures.length > 0) {
              logger.error(
                `Failed to delete ${failures.length} media items:`,
                failures,
              );
              throw new Error(
                `Failed to delete ${failures.length} media items`,
              );
            }

            logger.log(
              `Successfully deleted ${result.results.length} media items`,
            );

            // Sync FLC - remove deleted items from each category
            const deletedKeySet = new Set(keys);

            // Group by category and update FLC
            const categoriesAffected = new Set(
              itemsToDelete.map((item) => item.MEDIA_CATEGORY),
            );

            for (const category of categoriesAffected) {
              // Map category string to MediaCategory enum
              let mediaCategory: MediaCategory | undefined;
              if (category === 'Photo') {
                mediaCategory = MediaCategory.Photo;
              } else if (category === 'Document') {
                mediaCategory = MediaCategory.Document;
              } else if (category === 'Floor Plan') {
                mediaCategory = MediaCategory.FloorPlan;
              } else if (
                category === 'Unbranded Virtual Tour' ||
                category === 'Branded Virtual Tour'
              ) {
                mediaCategory = MediaCategory.Tour;
              }

              if (mediaCategory) {
                // Filter out deleted items from FLC media for this category
                const currentFlcMedia = (flcMedia as any[]).filter(
                  (m) => m.MEDIA_CATEGORY === category,
                );
                const updatedMedia = currentFlcMedia.filter(
                  (m) => !deletedKeySet.has(m.MED_VW_KEY),
                );

                logger.log('Syncing delete to FLC:', {
                  category: mediaCategory,
                  before: currentFlcMedia.length,
                  after: updatedMedia.length,
                });

                updateMediaByCategory({
                  category: mediaCategory,
                  resources: updatedMedia,
                });
              }
            }
          }
        }
      } catch (error) {
        logger.error('Failed to delete media:', error);
        throw error;
      }

      if (includesOEDocument) {
        updateFromListingStateAfter(100);
      }
    },
    [
      listingKey,
      cancelMediaJob,
      deleteMedia,
      items,
      flcMedia,
      updateMediaByCategory,
      logger,
    ],
  );

  const handleRefresh: RefreshHandler = useCallback(async () => {
    // TODO: Implement refresh logic
    return [];
  }, []);

  const handleRetryJob: RetryJobHandler = useCallback(
    async (items) => {
      try {
        // Process retries sequentially to avoid overwhelming the server
        await retryMediaJob(
          items.map((o) => ({
            jobId: o.jobId,
          })),
          listingKey,
        );
      } catch (error) {
        logger.error('Failed to retry job:', error);
        throw error;
      }
    },
    [retryMediaJob, listingKey, logger],
  );

  const handleRetryAiScan: RetryAiScanHandler = useCallback(
    async (item, jobId, action) => {
      try {
        const result = await retryAiScan(jobId, listingKey, action);
        logger.log('Retry AI scan result:', { jobId, action, result });
      } catch (error) {
        logger.error('Failed to retry AI scan:', error);
        throw error;
      }
    },
    [retryAiScan, listingKey, logger],
  );

  const handleAddVirtualTour: AddVirtualTourHandler = useCallback(
    async (name, url) => {
      try {
        logger.log('Creating virtual tour:', { listingKey, name, url });
        const result = await saveListingTour(listingKey, name, url);

        if (!result.success) {
          throw new Error(result.error || 'Failed to create virtual tour');
        }

        logger.log('Virtual tour created:', {
          mediaKey: result.mediaKey,
          media: result.media,
        });

        // Sync to FLC so V1 components see the new tour
        const tourCategory =
          result.media.MEDIA_CATEGORY || 'Unbranded Virtual Tour';
        let mediaCategory: MediaCategory | undefined;
        if (
          tourCategory === 'Unbranded Virtual Tour' ||
          tourCategory === 'Branded Virtual Tour'
        ) {
          mediaCategory = MediaCategory.Tour;
        }

        if (mediaCategory) {
          const currentFlcTours = (flcMedia as any[]).filter(
            (m) => m.MEDIA_CATEGORY === tourCategory,
          );
          updateMediaByCategory({
            category: mediaCategory,
            resources: [...currentFlcTours, result.media],
          });
          logger.log('Synced new tour to FLC:', {
            category: mediaCategory,
            tourCount: currentFlcTours.length + 1,
          });
        }

        // Return the created media resource in NGW format
        return result.media;
      } catch (error) {
        logger.error('Failed to create virtual tour:', error);
        throw error;
      }
    },
    [listingKey, saveListingTour, flcMedia, updateMediaByCategory, logger],
  );

  return {
    onInitiateUpload: handleInitiateUpload,
    onUploadFile: handleUploadFile,
    onSaveMetadata: handleSaveMetadata,
    onSaveOrder: handleSaveOrder,
    onDelete: handleDelete,
    onRetryJob: handleRetryJob,
    onRetryAiScan: handleRetryAiScan,
    onAddVirtualTour: handleAddVirtualTour,
  };
}
